/**
 * IAMONEAI Streaming Service
 * Handles Server-Sent Events (SSE) for real-time chat
 * 
 * @version 3.0
 */

// Streaming endpoint (Lambda Function URL)
const STREAM_URL = 'https://2srbc2y553vvamtvc5haoa3kaa0dkgtj.lambda-url.us-east-1.on.aws/';

/**
 * Send a message and stream the response token-by-token
 * 
 * @param {Object} params
 * @param {string} params.text - User message
 * @param {string} params.iin - User's IIN
 * @param {function} params.onToken - Callback for each token
 * @param {function} params.onStart - Callback when stream starts
 * @param {function} params.onComplete - Callback with metadata when done
 * @param {function} params.onError - Callback for errors
 * @returns {function} Abort function to cancel the stream
 */
export async function streamMessage({ text, iin, onToken, onStart, onComplete, onError }) {
  const abortController = new AbortController();
  
  try {
    const response = await fetch(STREAM_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ text, iin }),
      signal: abortController.signal,
    });

    if (!response.ok) {
      throw new Error(`Stream error: ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let fullResponse = '';

    while (true) {
      const { done, value } = await reader.read();
      
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      
      // Process complete SSE messages
      const lines = buffer.split('\n');
      buffer = lines.pop() || ''; // Keep incomplete line in buffer

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        
        const data = line.slice(6); // Remove "data: "
        
        if (data === '[DONE]') {
          continue;
        }

        try {
          const parsed = JSON.parse(data);

          // Handle different message types
          if (parsed.status === 'started') {
            onStart?.(parsed);
          } else if (parsed.token) {
            fullResponse += parsed.token;
            onToken?.(parsed.token, fullResponse);
          } else if (parsed.status === 'complete') {
            onComplete?.({
              ...parsed,
              fullResponse,
            });
          } else if (parsed.done) {
            // Final timing message
            onComplete?.((prev) => ({ ...prev, timing: parsed.timing }));
          } else if (parsed.error) {
            throw new Error(parsed.error);
          }
        } catch (parseError) {
          // Skip malformed JSON
          console.warn('SSE parse warning:', parseError.message);
        }
      }
    }

  } catch (error) {
    if (error.name === 'AbortError') {
      console.log('Stream aborted by user');
    } else {
      console.error('Stream error:', error);
      onError?.(error);
    }
  }

  // Return abort function
  return () => abortController.abort();
}

/**
 * Check if streaming is supported in this browser
 */
export function isStreamingSupported() {
  return typeof ReadableStream !== 'undefined' && 
         typeof TextDecoder !== 'undefined';
}

/**
 * Get the streaming endpoint URL
 */
export function getStreamUrl() {
  return STREAM_URL;
}

export default {
  streamMessage,
  isStreamingSupported,
  getStreamUrl,
};
